import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { isValidFileType } from '@/types/upload'

export interface UseDocumentDropOptions {
  enabled: boolean
}

export interface UseDocumentDropReturn {
  isDragging: boolean
  invalidDrop: boolean
  containerProps: {
    onDragEnter: (e: React.DragEvent) => void
    onDragLeave: (e: React.DragEvent) => void
    onDragOver: (e: React.DragEvent) => void
    onDrop: (e: React.DragEvent) => void
  }
}

export function useDocumentDrop(options: UseDocumentDropOptions): UseDocumentDropReturn {
  const { enabled } = options
  const [isDragging, setIsDragging] = useState(false)
  const [invalidDrop, setInvalidDrop] = useState(false)
  const dragCounterRef = useRef(0)
  const navigate = useNavigate()

  const onDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!enabled) return

      dragCounterRef.current++
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true)
      }
    },
    [enabled]
  )

  const onDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!enabled) return

      dragCounterRef.current--
      if (dragCounterRef.current === 0) {
        setIsDragging(false)
      }
    },
    [enabled]
  )

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
    },
    []
  )

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!enabled) return

      setIsDragging(false)
      dragCounterRef.current = 0

      const files = Array.from(e.dataTransfer.files)
      const validFiles = files.filter(isValidFileType)

      if (validFiles.length > 0) {
        setInvalidDrop(false)
        navigate('/upload', { state: { files: validFiles } })
      } else {
        setInvalidDrop(true)
      }
    },
    [enabled, navigate]
  )

  return {
    isDragging,
    invalidDrop,
    containerProps: {
      onDragEnter,
      onDragLeave,
      onDragOver,
      onDrop,
    },
  }
}
